"use client"

import { useState } from "react"
import { Category, Project, Subcategory } from "../../../lib/types"
import { Button } from "../../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { ArrowLeft, Eye, Plus, Trash2, FileText, Share } from "lucide-react"
import { SelectProjectsModal } from "./select-projects-modal"
import { formatSubcategoryPrice } from "./subcategory-info-modal"

interface SubcategoryDetailProps {
    category: Category
    subcategory: Subcategory
    allProjects: Project[]
    isShared: boolean
    onBack: () => void
    onRemoveProject: (projectId: string) => void
    onAddProjects: (projectIds: string[]) => void
    onViewProject: (projectId: string) => void
}

export function SubcategoryDetail({
    category,
    subcategory,
    allProjects,
    isShared,
    onBack,
    onRemoveProject,
    onAddProjects,
    onViewProject,
}: SubcategoryDetailProps) {
    const [selectModalOpen, setSelectModalOpen] = useState(false)

    // Qualquer projeto pode ser adicionado direto numa subcategoria — não
    // precisa já estar na categoria principal antes. O onAddProjects (vindo
    // de categories.tsx) se encarrega de garantir que ele também passe a
    // fazer parte de category.projectIds.
    const subcategoryProjects = allProjects.filter((project) =>
        subcategory.projectIds.includes(project.id)
    )

    const handleAddProjects = (projectIds: string[]) => {
        const newProjectIds = projectIds.filter(
            (id) => !subcategory.projectIds.includes(id)
        )
        if (newProjectIds.length > 0) {
            onAddProjects(newProjectIds)
        }
    }

    // Link direto pra essa subcategoria: mesmo padrão do link de categoria
    // (?i=<user>&s=<categoria>), só que com "&sub=" apontando pra
    // subcategoria específica. Quem abrir o link vê só os projetos dela.
    const handleShareSubcategory = () => {
        const link =
            window.location.origin +
            "?i=" + localStorage.getItem("user_id") +
            "&s=" + category.id +
            "&sub=" + subcategory.id

        navigator.clipboard.writeText(link)
            .then(() => {
                (window as any)['toast']?.("Link copiado!", "info")
            })
            .catch((err) => {
                console.error("Erro ao copiar: ", err)
            })
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={onBack}>
                        <ArrowLeft className="h-5 w-5" />
                        <span className="sr-only">Voltar</span>
                    </Button>
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight">
                            {category.name} / {subcategory.name}
                        </h2>
                        <p className="text-muted-foreground">
                            {subcategoryProjects.length} projeto(s) vinculado(s)
                        </p>
                    </div>
                </div>
                <div className="flex">
                    <Button variant="outline" className="m-2" onClick={handleShareSubcategory}>
                        <Share className="mr-2 h-4 w-4" />
                        Compartilhar
                    </Button>
                    {!isShared ? (
                        <Button className="m-2" onClick={() => setSelectModalOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Adicionar Projetos
                        </Button>
                    ) : null}
                </div>
            </div>

            {subcategory.info && (subcategory.info.price || subcategory.info.fabric || subcategory.info.sleeves || subcategory.info.collar || subcategory.info.differentials || subcategory.info.benefits) ? (
                <Card>
                    <CardContent className="py-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                        {subcategory.info.price ? (
                            <div><span className="text-muted-foreground">Preço:</span> <span className="font-semibold">{formatSubcategoryPrice(subcategory.info.price, subcategory.info.currency)}</span></div>
                        ) : null}
                        {subcategory.info.fabric ? (
                            <div><span className="text-muted-foreground">Tecido:</span> {subcategory.info.fabric}</div>
                        ) : null}
                        {subcategory.info.sleeves ? (
                            <div><span className="text-muted-foreground">Mangas:</span> {subcategory.info.sleeves}</div>
                        ) : null}
                        {subcategory.info.collar ? (
                            <div><span className="text-muted-foreground">Gola:</span> {subcategory.info.collar}</div>
                        ) : null}
                        {subcategory.info.differentials ? (
                            <div className="w-full"><span className="text-muted-foreground">Diferenciais:</span> {subcategory.info.differentials}</div>
                        ) : null}
                        {subcategory.info.benefits ? (
                            <div className="w-full"><span className="text-muted-foreground">Brindes:</span> {subcategory.info.benefits}</div>
                        ) : null}
                    </CardContent>
                </Card>
            ) : null}

            {subcategoryProjects.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                        <h3 className="text-lg font-medium">Nenhum projeto vinculado</h3>
                        <p className="text-sm text-muted-foreground mt-1 mb-4">
                            Clique em &quot;Adicionar Projetos&quot; para vincular projetos a esta subcategoria.
                        </p>
                        {!isShared ? (
                            <Button onClick={() => setSelectModalOpen(true)}>
                                <Plus className="mr-2 h-4 w-4" />
                                Adicionar Projetos
                            </Button>
                        ) : null}
                    </CardContent>
                </Card>
            ) : (
                <div className="h-[85vh] overflow-y-auto pr-2">
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {subcategoryProjects.map((project) => (
                            <Card key={project.id} className="group overflow-hidden">
                                <div className="relative aspect-video">
                                    <img
                                        src={project.thumbnail || "/placeholder.svg"}
                                        alt={project.name}
                                        className="object-cover transition-transform group-hover:scale-105"
                                    />
                                    <div className="flex">
                                        <Button
                                            variant="secondary"
                                            size="icon"
                                            className="absolute top-2 right-12 h-8 w-8 transition-opacity shadow-md"
                                            onClick={() => onViewProject(project.id)}
                                        >
                                            <Eye className="h-4 w-4" />
                                            <span className="sr-only">Abrir projeto</span>
                                        </Button>
                                        {!isShared ? (
                                            <Button
                                                variant="secondary"
                                                size="icon"
                                                className="absolute top-2 right-2 h-8 w-8 transition-opacity shadow-md"
                                                onClick={() => onRemoveProject(project.id)}
                                            >
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                                <span className="sr-only">Remover da subcategoria</span>
                                            </Button>
                                        ) : null}
                                    </div>
                                </div>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base line-clamp-1">{project.name}</CardTitle>
                                </CardHeader>
                                <CardContent className="pt-0">
                                    <CardDescription className="text-sm line-clamp-2">
                                        {project.description}
                                    </CardDescription>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            <SelectProjectsModal
                open={selectModalOpen}
                onOpenChange={setSelectModalOpen}
                allProjects={allProjects}
                selectedProjectIds={subcategory.projectIds}
                onConfirm={handleAddProjects}
            />
        </div>
    )
}